import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  deleteArticleAction,
  loadUserCourseInteractionActionById,
  loadCourseById,
  loadCourseUsersAction,
  retrieveAdminArticlesAction,
  retrieveStudentsSolutionInCourseForFreeResponseAction,
  retrieveStudentsSolutionInCourseForMcqAction,
} from "../middleware/adminCourseMiddleware";
import { ArticleType, DataStatus } from "../types/LearnTypes";
import _ from "lodash";
import { PaginationType } from "../../types/paginationType";
import {
  AddMultipleUserListItemType,
  AdminArticleType,
  AdminCourseUserType,
  AdminGraderType,
} from "../types/adminCourseTypes";

export type CourseUserType = {
  userId: string;
  userProfileImg?: string;
  email: string;
  firstName: string;
  lastName: string;
  userName: string;
  userCourseIntId: string;
  progress: string;
  lastActivity: string;
  role: string;
  isCertificateSent?: boolean;
};

export interface AdminCourseState {
  courseStatus: DataStatus;
  courses: Array<AdminArticleType>;
  coursePagination: PaginationType;
  // -----------------------
  blogsStatus: DataStatus;
  blogs: Array<AdminArticleType>;
  blogPagination: PaginationType;
  // -----------------------
  gradingCourseStatus: DataStatus;
  gradingCourse: AdminArticleType | undefined;
  // ----
  gradingUsersListStatus: DataStatus;
  gradingUsersList: Array<AdminCourseUserType>;
  gradingUserListPagination: PaginationType;
  // ----
  gradingUserStatus: DataStatus;
  gradingUser: AdminCourseUserType | undefined;
  // ----
  gradingMcqSolutionsStatus: DataStatus;
  gradingMcqSolutions: Array<any>;
  gradingMcqSolutionsPagination: PaginationType;
  // ----
  gradingFreeResponseSolutionsStatus: DataStatus;
  gradingFreeResponseSolutions: Array<any>;
  gradingFreeResponseSolutionsPagination: PaginationType;
  // ----
  invitedUsersList: Array<AddMultipleUserListItemType>;
}
export const paginationPlaceHolder = {
  total: 0,
  page: 0,
  resultsPerPage: 10,
  numPages: 0,
};

const initialState: AdminCourseState = {
  courseStatus: DataStatus.loading,
  courses: [],
  coursePagination: paginationPlaceHolder,
  // ==========================
  blogsStatus: DataStatus.loading,
  blogs: [],
  blogPagination: paginationPlaceHolder,
  // ==========================

  gradingCourseStatus: DataStatus.loading,
  gradingCourse: undefined,
  // ==========================
  gradingUserStatus: DataStatus.loading,
  gradingUser: undefined,
  // ==========================
  gradingUsersListStatus: DataStatus.loading,
  gradingUsersList: [],
  gradingUserListPagination: paginationPlaceHolder,
  // ==========================
  gradingMcqSolutionsStatus: DataStatus.loading,
  gradingMcqSolutions: [],
  gradingMcqSolutionsPagination: paginationPlaceHolder,
  // ==========================
  gradingFreeResponseSolutionsStatus: DataStatus.loading,
  gradingFreeResponseSolutions: [],
  gradingFreeResponseSolutionsPagination: paginationPlaceHolder,
  // ==========================
  invitedUsersList: [],
};

const adminCourseSlice = createSlice({
  name: "adminArticle",
  initialState: initialState,
  reducers: {
    setAdminArticleStatus: (
      state,
      action: PayloadAction<{ status: DataStatus; articleType: ArticleType }>
    ) => {
      const { status, articleType } = action.payload;
      if (articleType === ArticleType.course) {
        state.courseStatus = status;
        if (status === DataStatus.failed) {
          state.courses = [];
        }
      }
      if (articleType === ArticleType.blog) {
        state.blogsStatus = status;
        if (status === DataStatus.failed) {
          state.blogs = [];
        }
      }
    },
    setAdminGraderStatus: (
      state,
      action: PayloadAction<{
        status: DataStatus;
        gradingType: AdminGraderType;
      }>
    ) => {
      const { status, gradingType } = action.payload;
      if (gradingType === AdminGraderType.gradingCourse) {
        state.gradingCourseStatus = status;
      }
      if (gradingType === AdminGraderType.gradingUsersList) {
        state.gradingUsersListStatus = status;
      }
      if (gradingType === AdminGraderType.gradingUser) {
        state.gradingUserStatus = status;
      }
      if (gradingType === AdminGraderType.gradingMcq) {
        state.gradingMcqSolutionsStatus = status;
      }
      if (gradingType === AdminGraderType.gradingFrq) {
        state.gradingFreeResponseSolutionsStatus = status;
      }
    },
    addUsersToInviteList: (
      state,
      action: PayloadAction<AddMultipleUserListItemType>
    ) => {
      const updatedInvitedUsersList = state.invitedUsersList.map((user) =>
        user.email === action.payload.email
          ? {
              ...user,
              status: action.payload.status,
              errorMessage: action?.payload?.errorMessage || "",
            }
          : user
      );
      const userExists = state.invitedUsersList.some(
        (user) => user.email === action.payload.email
      );
      state.invitedUsersList = userExists
        ? updatedInvitedUsersList
        : [...state.invitedUsersList, action.payload];
    },
    resetUserToInviteList: (state) => {
      state.invitedUsersList = [];
    },
  },
  extraReducers(builder) {
    // retrieve all courses that user is a collaborator

    builder.addCase(
      retrieveAdminArticlesAction.fulfilled,
      (
        state,
        action: PayloadAction<{
          data: {
            courses: Array<AdminArticleType>;
            pagination: PaginationType;
          };
          articleType: ArticleType;
        }>
      ) => {
        if (Array.isArray(action.payload.data.courses)) {
          if (action.payload.articleType === ArticleType.blog) {
            // TODO : if api sorting is implemented
            // then remove this sorting
            const sortedBlogs = _.orderBy(
              action.payload.data.courses,
              [(item: any) => item.createdAt],
              ["desc"]
            );
            state.blogPagination = action.payload.data.pagination;
            state.blogs = sortedBlogs;
            state.blogsStatus = DataStatus.success;
          }
          if (action.payload.articleType === ArticleType.course) {
            // TODO : if api sorting is implemented
            // then remove this sorting
            const sortedCourses = _.orderBy(
              action.payload.data.courses,
              [(item: any) => item.createdAt],
              ["desc"]
            ).map((course: AdminArticleType) => {
              let orgRef = "PUBLIC COURSE";
              if (course?.orgData?.orgName) {
                orgRef = course?.orgData?.orgName;
              }
              return { ...course, orgRef: orgRef };
            });
            state.coursePagination = action.payload.data.pagination;
            state.courses = sortedCourses;
            state.courseStatus = DataStatus.success;
          }
        }
      }
    );

    builder.addCase(
      retrieveStudentsSolutionInCourseForFreeResponseAction.pending,
      (state) => {}
    );
    builder.addCase(
      retrieveStudentsSolutionInCourseForFreeResponseAction.fulfilled,
      (state, action: PayloadAction<any>) => {}
    );
    builder.addCase(
      retrieveStudentsSolutionInCourseForFreeResponseAction.rejected,
      (state, action) => {}
    );
    // getting all mcq responses submitted by user
    builder.addCase(
      retrieveStudentsSolutionInCourseForMcqAction.pending,
      (state) => {}
    );
    builder.addCase(
      retrieveStudentsSolutionInCourseForMcqAction.fulfilled,
      (state, action: PayloadAction<any>) => {}
    );
    builder.addCase(
      retrieveStudentsSolutionInCourseForMcqAction.rejected,
      (state, action) => {}
    );
    // loading single course data
    builder.addCase(loadCourseById.pending, (state) => {
      state.gradingCourseStatus = DataStatus.loading;
      state.gradingCourse = undefined;
      state.gradingUsersListStatus = DataStatus.loading;
      state.gradingUsersList = [];
      state.gradingUserStatus = DataStatus.loading;
      state.gradingUser = undefined;
      state.gradingMcqSolutions = [];
      state.gradingFreeResponseSolutions = [];
      state.gradingMcqSolutionsPagination = paginationPlaceHolder;
      state.gradingFreeResponseSolutionsPagination = paginationPlaceHolder;
      state.gradingUserListPagination = paginationPlaceHolder;
    });
    builder.addCase(
      loadCourseById.fulfilled,
      (
        state,
        action: PayloadAction<{
          courseObj: AdminArticleType;
        }>
      ) => {
        const { courseObj } = action.payload;
        let orgRef = "PUBLIC COURSE";
        if (courseObj?.orgData?.orgName) {
          orgRef = courseObj?.orgData?.orgName;
        }
        const courseId = courseObj._id;
        const courseIndex = state.courses.findIndex(
          (c: AdminArticleType) => c._id === courseId
        );
        if (courseIndex !== -1) {
          state.courses[courseIndex] = { ...courseObj, orgRef: orgRef };
        } else {
          state.courses.push({ ...courseObj, orgRef: orgRef });
        }
        state.gradingCourse = { ...courseObj, orgRef: orgRef };
        state.gradingCourseStatus = DataStatus.success;
      }
    );
    builder.addCase(loadCourseById.rejected, (state, action) => {
      state.gradingCourseStatus = DataStatus.failed;
      state.gradingCourse = undefined;
      state.gradingUsersListStatus = DataStatus.failed;
      state.gradingUsersList = [];
      state.gradingUserStatus = DataStatus.failed;
      state.gradingUser = undefined;
      state.gradingMcqSolutions = [];
      state.gradingFreeResponseSolutions = [];
      state.gradingMcqSolutionsPagination = paginationPlaceHolder;
      state.gradingFreeResponseSolutionsPagination = paginationPlaceHolder;
      state.gradingUserListPagination = paginationPlaceHolder;
      state.gradingCourse = undefined;
    });

    builder.addCase(loadCourseUsersAction.pending, (state) => {
      state.gradingUsersListStatus = DataStatus.loading;
      state.gradingUsersList = [];
      state.gradingUserListPagination = paginationPlaceHolder;
    });
    builder.addCase(
      loadCourseUsersAction.fulfilled,
      (
        state,
        action: PayloadAction<{
          courseUsers: Array<AdminCourseUserType>;
          pagination: PaginationType;
          courseId: string;
        }>
      ) => {
        const {
          courseUsers,
          pagination,
          courseId: updatingCourseId,
        } = action.payload;
        if (courseUsers && pagination) {
          const updatedAt = Date.now();
          const courseIndex = state.courses.findIndex(
            (c: AdminArticleType) => c._id === updatingCourseId
          );
          if (courseIndex) {
            state.courses[courseIndex].userList = {
              courseUsers,
              pagination,
              timeStamp: updatedAt,
            };
          }
          const selectedCourse = state.gradingCourse;
          if (selectedCourse && selectedCourse._id === updatingCourseId) {
            state.gradingCourse = {
              ...selectedCourse,
              userList: { courseUsers, pagination, timeStamp: updatedAt },
            };
            state.gradingUsersList = courseUsers;
            state.gradingUserListPagination = pagination;
            const previousSelectedUserId = state?.gradingUser?._id;
            if (previousSelectedUserId) {
              const selectedUser = courseUsers.find(
                (user: AdminCourseUserType) =>
                  user._id === previousSelectedUserId
              );
              if (selectedUser) {
                state.gradingUser = selectedUser;
                state.gradingUserStatus = DataStatus.success;
              }
            } else if (courseUsers[0]) {
              state.gradingUser = courseUsers[0];
              state.gradingUserStatus = DataStatus.success;
            } else {
              state.gradingUser = undefined;
              state.gradingUserStatus = DataStatus.failed;
            }
          }
        }
        state.gradingUsersListStatus = DataStatus.success;
      }
    );
    builder.addCase(loadCourseUsersAction.rejected, (state) => {
      state.gradingUsersListStatus = DataStatus.failed;
      state.gradingUsersList = [];
      state.gradingUserListPagination = paginationPlaceHolder;
      state.gradingUser = undefined;
      state.gradingUserStatus = DataStatus.failed;
    });
    builder.addCase(loadUserCourseInteractionActionById.pending, (state) => {
      // state.gradingUserStatus = DataStatus.loading;
      // state.gradingUser = undefined;
      state.gradingMcqSolutions = [];
      state.gradingFreeResponseSolutions = [];
      state.gradingMcqSolutionsPagination = paginationPlaceHolder;
      state.gradingFreeResponseSolutionsPagination = paginationPlaceHolder;
    });
    builder.addCase(
      loadUserCourseInteractionActionById.fulfilled,
      (
        state,
        action: PayloadAction<{
          courseUserInteractionObject: AdminCourseUserType;
          courseId: string;
        }>
      ) => {
        const { courseUserInteractionObject, courseId: updatingCourseId } =
          action.payload;
        const courseIndex = state.courses.findIndex(
          (c: AdminArticleType) => c._id === updatingCourseId
        );
        if (courseIndex) {
          const { userList } = state.courses[courseIndex];
          if (userList) {
            const userIndex = userList.courseUsers.findIndex(
              (user: AdminCourseUserType) =>
                user._id === courseUserInteractionObject._id
            );
            if (userIndex !== -1) {
              userList.courseUsers[userIndex] = courseUserInteractionObject;
            }
            state.courses[courseIndex].userList = {
              ...userList,
              courseUsers: userList.courseUsers,
            };
          }
        }
        const userIndex = state.gradingUsersList.findIndex(
          (user: AdminCourseUserType) =>
            user._id === courseUserInteractionObject._id
        );
        if (userIndex !== -1) {
          state.gradingUsersList[userIndex] = courseUserInteractionObject;
        }
        state.gradingUser = courseUserInteractionObject;
        state.gradingUserStatus = DataStatus.success;
      }
    );
    builder.addCase(
      loadUserCourseInteractionActionById.rejected,
      (state, action) => {
        state.gradingUserStatus = DataStatus.failed;
        state.gradingUser = undefined;
        state.gradingMcqSolutions = [];
        state.gradingFreeResponseSolutions = [];
        state.gradingMcqSolutionsPagination = paginationPlaceHolder;
        state.gradingFreeResponseSolutionsPagination = paginationPlaceHolder;
      }
    );
    builder.addCase(deleteArticleAction.pending, (state) => {});
    builder.addCase(
      deleteArticleAction.fulfilled,
      (
        state,
        action: PayloadAction<{
          deletedArticleId: string;
          articleType: ArticleType;
        }>
      ) => {
        const { deletedArticleId, articleType } = action.payload;
        if (articleType === ArticleType.blog) {
          state.blogs = state.blogs.filter(
            (course: AdminArticleType) => course._id !== deletedArticleId
          );
          state.blogPagination.total = state.blogPagination.total - 1;
        }
        if (articleType === ArticleType.course) {
          state.coursePagination.total = state.coursePagination.total - 1;
          state.coursePagination.numPages = Math.ceil(
            state.coursePagination.total / state.coursePagination.resultsPerPage
          );
          state.courses = state.courses.filter(
            (course: AdminArticleType) => course._id !== deletedArticleId
          );
          if (state.gradingCourse?._id === deletedArticleId) {
            state.gradingCourse = undefined;
            state.gradingUsersList = [];
            state.gradingUser = undefined;
            state.gradingMcqSolutions = [];
            state.gradingFreeResponseSolutions = [];
            state.gradingMcqSolutionsPagination = paginationPlaceHolder;
            state.gradingFreeResponseSolutionsPagination =
              paginationPlaceHolder;
            state.gradingUserListPagination = paginationPlaceHolder;
            // if grading course is deleted then redirect to course page
            window.location.href = "/admin/course";
          }
        }
      }
    );
    builder.addCase(deleteArticleAction.rejected, (state) => {});
  },
});

export const {
  setAdminArticleStatus,
  setAdminGraderStatus,
  addUsersToInviteList,
  resetUserToInviteList,
} = adminCourseSlice.actions;
export default adminCourseSlice.reducer;
